"use client";

import dynamic from "next/dynamic";
import React, {useEffect, useState} from 'react';
import Link from 'next/link';
import Image from "next/image";
import {Badge} from "@/components/ui/badge";
import {VeicoloData} from "@/interface/VeicoloData";
import {formatPrice} from "@/utils/format-price";
import {useRouter, usePathname, useSearchParams} from "next/navigation";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import Cookies from "js-cookie";
import {useStoredCart} from "@/hooks/useStoredCart";
import { clearOperatorSessionArtifacts } from "@/utils/logout";

const DeferredSearchBoxHome = dynamic(() => import("@/components/Home/SearchBoxhome"), { ssr: false });


interface B2BNavbarProps {
    showEllipse?: boolean | undefined;
    isAuthenticated?: boolean;
}

const B2BNavbar: React.FC<B2BNavbarProps> = ({showEllipse = false}) => {

    const [cartOpen, setCartOpen] = useState(false);
    const { vehicles: vehiclesInCart, count: cartCount } = useStoredCart();
    const router = useRouter();

    const [isScrolled, setIsScrolled] = useState(false);

    const [menuOpen, setMenuOpen] = useState(false);

    const [leadFrom, setLeadFrom] = useState(false);
    const [telephone, setTelephone] = useState(false);
    const [isClient, setIsClient] = useState(false);

    const userData = Cookies.get("userData") ? JSON.parse(Cookies.get("userData")!) : null;
    const checkoutUrl = userData?.infoUtentiTipo === "COM" ? "/checkout-b2b" : "/checkout";
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const currentOrder = searchParams?.get("order") || "";
    const acquistaHref = currentOrder ? `/parco-veicoli?order=${encodeURIComponent(currentOrder)}` : "/parco-veicoli";


    useEffect(() => {
        // Indica che il componente è stato montato sul client
        setIsClient(true);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 0) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        // Add the scroll event listener when the component mounts
        window.addEventListener('scroll', handleScroll);

        // Clean up the event listener when the component unmounts
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
    };

    const toggleCart = () => {
        setCartOpen(!cartOpen);
    };

    const handleAccount = () => {
            window.location.href = `https://consorzioauto.com/dashboard`;
            return;
    }


    const handleLogout = async () => {
        try {
            await fetch("/api/auth/logout-cleanup", {
                method: "POST",
                credentials: "include",
            });
        } catch (error) {
            console.error("Logout cleanup failed", error);
        }

        clearOperatorSessionArtifacts();
        location.reload();
    };

    return (
        <>
            <div className={`z-40 fixed top-0 left-0 w-full max-lg:hidden transition duration-300 ease-in-out ${
                menuOpen || isScrolled ? 'backdrop-blur-md bg-white/30' : 'bg-transparent'
            }`}>
                {/* Top Bar */}
                <div className=" flex items-center justify-between px-4 py-3 border-b">
                    <div className="flex items-center gap-10">
                        <Link href='/'>
                            <Image
                                src="/logo_consorzio.png"
                                alt="Consorzio logo"
                                width={70} // Specifica la larghezza
                                height={70} // Specifica l'altezza
                                priority={true} // Precarica l'immagine perché è un elemento critico nella UI
                                className="h-10 w-auto" // Tailwind per aggiustare ulteriormente lo stile
                            />
                        </Link>

                        <nav className="flex items-center gap-6 text-lg font-medium">
                            <Link
                                hrefLang="it"
                                href={acquistaHref}
                                title="Parco Veicoli - Catalogo dei Veicoli Acquistabili"
                                className={`${showEllipse && !isScrolled ? 'text-white' : 'text-gray-800'} text-xl max-md:text-base hover:text-primary transition duration-300`}
                            >
                                Acquista
                            </Link>
                            <Link
                                hrefLang="it"
                                href="/noleggio-b2b"
                                title="Vendi la tua auto"
                                className={`${showEllipse && !isScrolled ? 'text-white' : 'text-gray-800'} text-xl hover:text-primary transition duration-300`}
                            >
                                Noleggio
                            </Link>
                        </nav>

                    </div>

                    <div className="flex items-center gap-4 relative">
                        <DeferredSearchBoxHome className="!py-1 mx-2"/>
                        <div className="relative" onClick={toggleCart}>
                            <svg width="27" height="25" viewBox="0 0 27 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M0.0183924 1.34385C0.119968 0.835973 0.614029 0.506599 1.12191 0.608175L2.43404 0.870601C3.91609 1.16701 5.03084 2.3964 5.18123 3.9003L5.28784 4.9664H22.2628C24.7439 4.9664 26.5644 7.29808 25.9626 9.70512L24.5587 15.3208C23.9673 17.6865 21.8417 19.3461 19.4031 19.3461H8.15829C6.41766 19.3461 4.95155 18.0454 4.74416 16.3172L3.50691 6.00674L3.31492 4.08693C3.24656 3.40334 2.73986 2.84453 2.0662 2.70979L0.754069 2.44737C0.24619 2.34579 -0.0831833 1.85173 0.0183924 1.34385ZM9.69081 8.65748C9.2766 8.65748 8.94081 8.99327 8.94081 9.40748C8.94081 9.82169 9.2766 10.1575 9.69081 10.1575H14.6925C15.1067 10.1575 15.4425 9.82169 15.4425 9.40748C15.4425 8.99327 15.1067 8.65748 14.6925 8.65748H9.69081Z" fill="#000D7D"/>
                                <circle cx="9.06543" cy="22.7847" r="1.87562" fill="#000D7D"/>
                                <circle cx="20.3192" cy="22.7847" r="1.87562" fill="#000D7D"/>
                            </svg>
                                {isClient && cartCount > 0 && (
                                    <Badge className="absolute -top-2 -right-2 !px-2 text-white border-0">
                                        {cartCount}
                                    </Badge>
                                )}
                        </div>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <div className="relative cursor-pointer">
                                    <svg
                                        className="w-8 h-8"
                                        width="45"
                                        height="44"
                                        viewBox="0 0 45 44"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <g clipPath="url(#clip0_578_51)">
                                            <rect x="0.222656" width="44" height="44" rx="22" fill="#000D7D" />
                                            <circle
                                                cx="8.11429"
                                                cy="8.11429"
                                                r="8.11429"
                                                transform="matrix(-1 0 0 1 30.337 11)"
                                                fill="#A6C2FF"
                                            />
                                            <path
                                                d="M8.02344 39.176C8.02344 37.4856 9.08612 35.9776 10.6781 35.4091L11.1243 35.2497C18.3017 32.6863 26.1451 32.6863 33.3225 35.2497L33.7688 35.4091C35.3608 35.9776 36.4234 37.4856 36.4234 39.176V44.194C36.4234 45.4111 35.3455 46.346 34.1406 46.1739L26.0418 45.0169C23.5091 44.6551 20.9378 44.6551 18.4051 45.0169L10.3063 46.1739C9.10141 46.346 8.02344 45.4111 8.02344 44.194V39.176Z"
                                                fill="#A6C2FF"
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_578_51">
                                                <rect x="0.222656" width="44" height="44" rx="22" fill="white" />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </div>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem onClick={handleAccount}>
                                    Dashboard
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                                    Esci
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {/*Cart Dropdown*/}
                        {cartOpen && (
                            <div className="absolute right-1/2 top-[100%] mt-2 w-64 rounded-lg bg-white/90 p-1.5 shadow-lg z-10 transition-opacity duration-200">
                                {vehiclesInCart.length === 0 ? (
                                    <div className="text-center p-4">
                                        <p className="text-sm font-bold text-black">Il carrello è vuoto</p>
                                    </div>
                                ) : (
                                    vehiclesInCart.map((vehicle: VeicoloData, index) => (
                                        <div key={index}
                                             className="rounded bg-white flex justify-between items-center p-2">
                                            <div className="flex-none relative">

                                                <p className="absolute top-0 left-0 text-[10px] px-1 text-[#979797]">1x</p>

                                                <Image
                                                    src={vehicle.images.find((image) => image.defaultimage == '1')?.image || '/autoinarrivo.webp'}
                                                    alt={vehicle.marca + ' ' + vehicle.modello}
                                                    className="rounded-lg object-cover"
                                                    width={50} // Adjusted to better match the intended width
                                                    height={50} // Adjusted to keep aspect ratio
                                                    style={{
                                                        width: '50px',
                                                        height: 'auto'
                                                    }} // Responsive styling
                                                />
                                            </div>

                                            <div className="flex flex-col justify-center flex-grow pl-2">
                                                <span className="text-xs leading-tight font-light">
                                                    {vehicle.marca}
                                                </span>
                                                <span className="text-sm font-bold">{vehicle.modelloAbr}</span>
                                            </div>

                                            <div className="flex-none">
                        <span
                            className="text-sm font-normal">{formatPrice(+vehicle.prezzo)}</span>
                                            </div>
                                        </div>
                                    ))
                                )}

                                {vehiclesInCart.length > 0 && (
                                    <div className="w-full pt-2 rounded">
                                        <Link onClick={toggleCart}
                                              hrefLang={'it'}
                                              href={checkoutUrl}
                                              title={'Carrello'}
                                              className="block w-full rounded text-white bg-gradient p-1 text-center"
                                        >
                                            Vai al checkout
                                        </Link>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default B2BNavbar;
